import React, { useEffect, useState, useRef } from 'react';
import { apiFetch } from '../lib/api';

// Pillar 1 — auto-bio surface for events that reference a person.
//
// Two-stage UX:
//   1. Detect — given an event title (+ notes), ask the server's regex
//      whether there's a person reference. Returns { name, org } or
//      null. Cheap, no Claude tokens.
//   2. Lookup — when the user clicks "Show bio", fetch the structured
//      bio (Claude + web search). Cached server-side, so subsequent
//      opens of the same event hit warm.
//
// Renders nothing when no person is detected — keeps the editor
// compact for the 90% of events that don't reference one. Bio fetch
// is opt-in (button click) so we don't auto-burn tokens on every
// modal open.

export default function PersonBioCard({ title, notes }) {
  const [person, setPerson] = useState(null);
  const [bio, setBio] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const detectTimer = useRef(null);

  // Debounced detect when title or notes change. Title typing
  // shouldn't fire a new request on every keystroke.
  useEffect(() => {
    clearTimeout(detectTimer.current);
    detectTimer.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (title) params.set('title', title);
        if (notes) params.set('notes', notes);
        const data = await apiFetch(`/people/detect?${params.toString()}`);
        setPerson(data.person || null);
        // If the detected person changes, stale bio data shouldn't
        // linger from the previous one.
        setBio(null);
        setOpen(false);
      } catch {
        setPerson(null);
      }
    }, 350);
    return () => clearTimeout(detectTimer.current);
  }, [title, notes]);

  const fetchBio = async () => {
    if (!person?.name) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ name: person.name });
      if (person.org) params.set('org', person.org);
      const data = await apiFetch(`/people/lookup?${params.toString()}`);
      if (!data.found || data.bio?.found === false) {
        setError(`Couldn't find a confident match for ${person.name}.`);
        setBio(null);
      } else {
        setBio(data.bio);
      }
      setOpen(true);
    } catch (e) {
      setError(e.message || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  if (!person) return null;

  return (
    <div
      style={{
        marginTop: 10,
        padding: '8px 10px',
        background: 'rgba(99, 102, 241, 0.06)',
        border: '1px solid rgba(99, 102, 241, 0.3)',
        borderRadius: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <i className="fa-solid fa-user-tie" style={{ color: '#4f46e5', fontSize: 12 }} />
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: '#4338ca' }}>
          PERSON
        </span>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
          {person.name}
        </span>
        {person.org && (
          <span style={{ fontSize: 11, color: 'var(--text2)' }}>· {person.org}</span>
        )}
        <div style={{ flex: 1 }} />
        {!open && !loading && (
          <button
            onClick={fetchBio}
            style={{
              background: '#4f46e5',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '3px 9px',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.5,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <i className="fa-solid fa-wand-magic-sparkles" style={{ marginRight: 5 }} />
            BIO
          </button>
        )}
        {loading && (
          <span style={{ fontSize: 10, color: 'var(--text3)', fontStyle: 'italic' }}>
            researching…
          </span>
        )}
        {open && bio && (
          <button onClick={() => setOpen(false)} style={linkBtn}>collapse</button>
        )}
      </div>

      {open && error && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#b45309' }}>
          {error}
        </div>
      )}

      {open && bio && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(99,102,241,0.18)' }}>
          {(bio.role || bio.org) && (
            <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>
              {[bio.role, bio.org].filter(Boolean).join(' · ')}
              {bio._stale && (
                <span style={{ marginLeft: 8, fontSize: 9, color: '#b45309', fontWeight: 700 }}>
                  STALE
                </span>
              )}
            </div>
          )}
          {bio.summary && (
            <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.45, marginBottom: 6 }}>
              {bio.summary}
            </div>
          )}
          {Array.isArray(bio.recentWork) && bio.recentWork.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1, color: 'var(--text3)', marginBottom: 3 }}>
                RECENT
              </div>
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: 'var(--text2)', lineHeight: 1.5 }}>
                {bio.recentWork.slice(0, 4).map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          {Array.isArray(bio.topicsCovered) && bio.topicsCovered.length > 0 && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
              {bio.topicsCovered.slice(0, 6).map((t, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: 10,
                    color: 'var(--text2)',
                    background: 'var(--bg3)',
                    border: '1px solid var(--border)',
                    borderRadius: 999,
                    padding: '1px 7px',
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          )}
          {bio.notable && (
            <div style={{ fontSize: 11, color: 'var(--text2)', fontStyle: 'italic', marginBottom: 6 }}>
              <i className="fa-solid fa-lightbulb" style={{ marginRight: 5, color: '#f59e0b' }} />
              {bio.notable}
            </div>
          )}
          {Array.isArray(bio.sources) && bio.sources.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {bio.sources.slice(0, 3).map((src, i) => (
                <a
                  key={i}
                  href={src}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 10, color: '#4f46e5', textDecoration: 'none' }}
                >
                  source {i + 1} ↗
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const linkBtn = {
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: 11,
  fontWeight: 600,
  color: '#4338ca',
  cursor: 'pointer',
  fontFamily: 'inherit',
};
